import { postJson } from "./http"
import { canvasResize, BLUR_PLACEHOLDER } from "./image_resize"
import { isObjectionable } from "./nsfw"

// Upload thumbnails for the given client_refs to the server, in small chunks.
//
// Shared by every backfill path (main-library native, feed, and private-session
// native). Posting hundreds of base64 thumbnails in a single request spiked the
// web server's memory (OOM), so we cap each request at `chunkSize`.
//
// fetchOne(clientRef) must resolve to a base64 data URI string, or null/undefined
// to skip that photo. The per-photo fetch differs per caller (bridge vs canvas),
// so it's injected; the chunking + upload is the shared part.
export async function uploadThumbnailsInChunks(clientRefs, fetchOne, { chunkSize = 25 } = {}) {
  if (!clientRefs || clientRefs.length === 0) return

  let batch = []
  const flush = async () => {
    if (batch.length === 0) return
    await postJson("/library_photos/thumbnails", { thumbnails: batch })
    batch = []
  }

  for (const ref of clientRefs) {
    const data = await fetchOne(ref)
    if (data) {
      // Objectionable-content filter: if a matched photo is flagged explicit, tell
      // the server to drop its matches + exclude it from future matching (it won't
      // create matches), and don't upload its thumbnail. Fail-open inside isObjectionable.
      if (await isObjectionable(data)) {
        try { await postJson("/library_photos/flag_explicit", { client_ref: ref }) } catch (e) {}
        if (batch.length >= chunkSize) await flush()
        continue
      }
      // Tiny blurred stand-in served to the other person until this photo's owner
      // approves it (so the real image is never sent unapproved).
      const blur = await canvasResize(data, BLUR_PLACEHOLDER)
      batch.push({ client_ref: ref, data, blur })
    }
    if (batch.length >= chunkSize) await flush()
  }
  await flush()
}
